import {gql} from '@apollo/client';
import {Heading, Pane, TickCircleIcon} from 'evergreen-ui';
import React, {useEffect, useReducer} from 'react';
import {
    Column,
    EmailLabelWithCopy,
    EnrichedTable,
    ErrorDisplay,
    LoadingSpinner,
    PORTAL_NAVY,
    RelayConnection,
    SimpleDropdown,
    SortBy,
} from '../../../common';
import {formatISODateStr} from '../../../common/util/date';
import {useLazyQuery$} from '../../../graphqlsupport';
import {SortOrder} from '../../../placement/domains';
import {useObservable} from '../../../rxsupport';
import {
    RelayPagination,
    RelayPaginationActionPayload,
    RelayPaginationState,
    reduceRelayPaginationAction,
    useRelayPagination,
} from '../../pagination';
import {describeFilters, describeSearch} from './describeFilters';
import {ExportAuditRow, MODULE_LABELS} from './domain/types';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
const STATUS_GREEN = '#52BD94';
const STATUS_RED = '#D14343';
/** Nothing to show is a fact here (no filters, count not yet tallied) - never a blank cell. */
const EMPTY_CELL = '-';

/** Fills the two columns the stored filter snapshot yields, once per row rather than once per cell. */
const withDescribedFilters = (row: ExportAuditRow): ExportAuditRow => ({
    ...row,
    filterLines: describeFilters(row.module, row.filtersJson),
    searchText: describeSearch(row.filtersJson),
});

const STATUS_LABELS: Record<ExportAuditRow['status'], string> = {
    QUEUED: 'Queued',
    RUNNING: 'Running',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
};

// FE column attr → BE whitelist sort key (ExportQueryAdapter#resolveSort). A column absent from this
// map is not sortable: the map is the guard.
const SORT_KEY_BY_COLUMN: Partial<Record<keyof ExportAuditRow, string>> = {
    requestedAt: 'requestedAt',
    requestedByFirstName: 'requestedBy',
    module: 'module',
};
const DEFAULT_SORT: SortBy<ExportAuditRow> = {column: 'requestedAt', order: SortOrder.DESC};

export const EXPORT_AUDIT_LOG_QUERY = gql`
    query exportAuditLog($first: Int = 10, $after: String, $sortBy: String, $sortDir: String) {
        exportAuditLog(first: $first, after: $after, sortBy: $sortBy, sortDir: $sortDir) {
            edges {
                cursor
                node {
                    guid
                    requestedAt
                    requestedByFirstName
                    requestedByLastName
                    requestedByEmail
                    module
                    filtersJson
                    recordCount
                    status
                    errorMessage
                }
            }
            totalElements
            pageInfo {
                hasPreviousPage
                endCursor
                hasNextPage
                startCursor
            }
        }
    }
`;

type AuditLogResponse = RelayConnection<ExportAuditRow> & {totalElements: number};

type AuditLogState = {pagination: RelayPaginationState; pageSize: number; sortBy: SortBy<ExportAuditRow>};
type AuditLogAction =
    | RelayPaginationActionPayload
    | {type: 'SET_PAGE_SIZE'; pageSize: number}
    | {type: 'SET_SORT_BY'; sortBy: SortBy<ExportAuditRow>};

export function auditLogReducer(state: AuditLogState, action: AuditLogAction): AuditLogState {
    switch (action.type) {
        case 'RELAY_PAGINATION_ACTION':
            return {...state, pagination: reduceRelayPaginationAction(state.pagination, action)};
        case 'SET_PAGE_SIZE':
            // Page-size change resets to the first page.
            return {...state, pageSize: action.pageSize, pagination: new RelayPaginationState('0', action.pageSize)};
        case 'SET_SORT_BY':
            // Sort change resets to the first page.
            return {...state, sortBy: action.sortBy, pagination: new RelayPaginationState('0', state.pageSize)};
        default:
            return state;
    }
}

const ResultCell = ({row}: {row: ExportAuditRow}) => {
    const label = STATUS_LABELS[row.status];
    if (row.status === 'COMPLETED') {
        return (
            <span className="inline-flex items-center" style={{color: STATUS_GREEN}}>
                <TickCircleIcon size={14} color={STATUS_GREEN} marginRight={6}/>
                {label}
            </span>
        );
    }
    if (row.status === 'FAILED') {
        return (
            <span style={{color: STATUS_RED}}>
                {label}
                {row.errorMessage && <div className="text-xs">{row.errorMessage}</div>}
            </span>
        );
    }
    return <span>{label}</span>;
};

export const ExportAuditLogTable = () => {
    const [{pagination, pageSize, sortBy}, dispatch] = useReducer(auditLogReducer, {
        pagination: new RelayPaginationState('0', DEFAULT_PAGE_SIZE),
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy: DEFAULT_SORT,
    });

    const sortVars = () => ({
        sortBy: SORT_KEY_BY_COLUMN[sortBy.column as keyof ExportAuditRow] ?? 'requestedAt',
        sortDir: sortBy.order,
    });

    const {execute, data, isRunning$, error} = useLazyQuery$<
        {first: number; after: string; sortBy: string; sortDir: string},
        {exportAuditLog: AuditLogResponse}
    >(EXPORT_AUDIT_LOG_QUERY, {...pagination.toGraphQlParams(), ...sortVars()});

    useEffect(() => {
        execute({...pagination.toGraphQlParams(), ...sortVars()});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, sortBy]);

    const loading = useObservable(isRunning$, true);
    const totalRows = data?.exportAuditLog?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalRows);
    const rows = data?.exportAuditLog?.edges.map((edge) => withDescribedFilters(edge.node)) ?? [];

    if (error) {
        return <ErrorDisplay error={error} title="Error loading export audit log"/>;
    }

    return (
        <Pane className="flex flex-col" flexGrow={1}>
            <Pane display="flex" justifyContent="flex-end" alignItems="center" marginBottom={16}>
                <SimpleDropdown options={PAGE_SIZE_OPTIONS} selected={pageSize.toString()}
                                minWidth={'2rem'}
                                textColor={'gray-700'}
                                setSelected={(newPageSize) => dispatch({type: 'SET_PAGE_SIZE', pageSize: parseInt(newPageSize)})}/>
                <RelayPagination pageInfo={data?.exportAuditLog.pageInfo}
                                 count={totalRows}
                                 onClick={onPaginationClick}/>
            </Pane>
            <Pane borderTop="1px solid #E6E8EC" marginBottom={20}/>
            <Heading size={600} color={PORTAL_NAVY} marginTop={8} marginBottom={20}>Export History</Heading>
            <EnrichedTable<ExportAuditRow>
                firstColumn={'requestedAt'}
                sortBy={sortBy}
                setSortBy={(col: Column<ExportAuditRow>) => {
                    if (!SORT_KEY_BY_COLUMN[col.attr as keyof ExportAuditRow]) {
                        return;
                    }
                    // First click on a column sorts desc; clicking the active desc column flips to asc.
                    const order = col.attr === sortBy.column && sortBy.order === SortOrder.DESC
                        ? SortOrder.ASC
                        : SortOrder.DESC;
                    dispatch({type: 'SET_SORT_BY', sortBy: {column: col.attr, order}});
                }}
                styleOverride={{
                    trHeader: 'portal-history-header text-left select-none text-sm',
                }}
                columns={[
                    {attr: 'requestedAt', name: 'Date/Time', span: 2},
                    {attr: 'requestedByFirstName', name: 'User', span: 2},
                    {attr: 'module', name: 'Page', span: 2},
                    {attr: 'filterLines', name: 'Filters Applied', span: 3},
                    {attr: 'searchText', name: 'Search Criteria', span: 2},
                    {attr: 'recordCount', name: 'Records', span: 1},
                    {attr: 'status', name: 'Result', span: 2},
                ]}
                rowsData={rows}
                keyColumn={'guid'}
                cellDisplayFunc={(row, column) => {
                    if (column.attr === 'requestedAt') {
                        return formatISODateStr(row.requestedAt, 'MM/dd/yyyy hh:mm a');
                    }
                    if (column.attr === 'requestedByFirstName') {
                        const name = `${row.requestedByFirstName ?? ''} ${row.requestedByLastName ?? ''}`.trim();
                        // The schema leaves the requester nullable; degrade like every other cell
                        // rather than showing a blank with a live copy button.
                        return name === ''
                            ? EMPTY_CELL
                            : <EmailLabelWithCopy email={row.requestedByEmail} label={name}/>;
                    }
                    if (column.attr === 'module') {
                        return MODULE_LABELS[row.module] ?? row.module;
                    }
                    if (column.attr === 'filterLines') {
                        const lines = row.filterLines ?? [];
                        return lines.length === 0
                            ? EMPTY_CELL
                            : <>{lines.map((line) => <div key={line}>{line}</div>)}</>;
                    }
                    if (column.attr === 'searchText') {
                        return row.searchText ?? EMPTY_CELL;
                    }
                    if (column.attr === 'recordCount') {
                        return row.recordCount === null || row.recordCount === undefined
                            ? EMPTY_CELL
                            : `${row.recordCount}`;
                    }
                    if (column.attr === 'status') {
                        return <ResultCell row={row}/>;
                    }
                    return '';
                }}
                emptyMessage={
                    <div className="w-full flex items-center">
                        <div className="m-auto pt-8">No exports yet</div>
                    </div>
                }
                loadingFeat={{
                    loading,
                    loadingDisplayElement: <LoadingSpinner spacing={20} height={'30vh'}/>,
                }}
            />
        </Pane>
    );
};
